// Zentrales Conversion-Messung-Modul: Google-Ads-Conversions + GA4-Events.
// gtag.js lädt zentral im <head> der index.html (async, mit Consent-Mode-v2-
// Defaults „denied"); der CookieBanner setzt bei Einwilligung consent=update
// „granted". ALLE Conversion-Aufrufe der Website laufen über diese Datei.
//
// Hinweis zu den IDs: Ads-ID und Conversion-Labels sind öffentliche Client-
// Kennungen (sie stehen im ausgelieferten JavaScript jeder Ads-Website).
import axios from "axios";
import { trackMeta } from "./metaPixel";

export const ADS_ID = "AW-348860265";
const LABEL_FORM = "zfKMCMvcn6gdEOnerKYB"; // Conversion „Formular-Lead"
const LABEL_ANRUF = "-ti4CIzL2awdEOnerKYB"; // Conversion „Anruf-Klick"
const LABEL_WHATSAPP = "2EBsCI_L2awdEOnerKYB"; // Conversion „WhatsApp-Klick"
const CONSENT_KEY = "ue24_consent";

export function hasTrackingConsent() {
  try { return localStorage.getItem(CONSENT_KEY) === "accepted"; } catch { return false; }
}

const gtagReady = () => typeof window.gtag === "function";
const ready = () => hasTrackingConsent() && gtagReady();

// Stadt aus dem Seitenpfad ableiten (z. B. /kfz-gutachter-krefeld → krefeld,
// /kfz-gutachter-koeln/ehrenfeld → koeln) – Event-Parameter für GA4/Ads.
export function cityFromPath(pathname = window.location.pathname) {
  const m = pathname.match(/kfz-gutachter-([a-z]+)/);
  return m ? m[1] : "nrw";
}

// Einheitliche Event-Parameter (Aufgabe 9): page_location + city bei jedem Event
const eventParams = () => ({ page_location: window.location.href, city: cityFromPath() });

// SPA: bei jedem Routenwechsel Seitenaufruf an Google Ads melden (dedupliziert)
let _lastPagePath = null;
export function gtagPageConfig(path) {
  if (!path || _lastPagePath === path || !gtagReady()) return;
  _lastPagePath = path;
  window.gtag("config", ADS_ID, { page_path: path });
}

// Telefon in E.164 (+49 …) für Erweiterte Conversions normalisieren
function toE164(phone) {
  let t = String(phone || "").replace(/[^0-9+]/g, "");
  if (t.startsWith("00")) t = `+${t.slice(2)}`;
  if (t.startsWith("0")) t = `+49${t.slice(1)}`;
  return t.startsWith("+") && t.length >= 8 ? t : "";
}

// Formular-Lead: NUR nach erfolgreichem Absenden (Backend 200/201) aufrufen.
// Erweiterte Conversions (E-Mail/Telefon – gtag hasht vor dem Senden) feuern
// nur bei erteilter Einwilligung; transaction_id verhindert Doppelzählung.
export function trackFormLead({ email = "", phone = "", leadId = "" } = {}) {
  if (!ready()) return;
  const userData = {};
  if (email && /\S+@\S+\.\S+/.test(email)) userData.email = email.trim().toLowerCase();
  const e164 = toE164(phone);
  if (e164) userData.phone_number = e164;
  if (Object.keys(userData).length) window.gtag("set", "user_data", userData);
  const params = eventParams();
  window.gtag("event", "generate_lead", params);
  window.gtag("event", "callback_request", params);
  window.gtag("event", "conversion", {
    send_to: `${ADS_ID}/${LABEL_FORM}`,
    value: 1.0,
    currency: "EUR",
    ...params,
    ...(leadId ? { transaction_id: leadId } : {}),
  });
}

// Klick-Conversions (Anruf / WhatsApp): feuern nur nach Einwilligung
function trackAdsConversion(label) {
  if (!ready()) return;
  window.gtag("event", "conversion", { send_to: `${ADS_ID}/${label}`, value: 1.0, currency: "EUR", ...eventParams() });
}

// Klick auf Telefonnummer: click_to_call + Ads-Conversion + Meta Contact
export function trackPhoneClick(phoneNumber) {
  if (!hasTrackingConsent()) return;
  if (gtagReady()) window.gtag("event", "click_to_call", { phone_number: phoneNumber, ...eventParams() });
  trackAdsConversion(LABEL_ANRUF);
  trackMeta("Contact", { contact_type: "phone" });
}

// Klick auf WhatsApp: whatsapp_click + Ads-Conversion + Meta Contact
export function trackWhatsappClick() {
  if (!hasTrackingConsent()) return;
  if (gtagReady()) window.gtag("event", "whatsapp_click", eventParams());
  trackAdsConversion(LABEL_WHATSAPP);
  trackMeta("Contact", { contact_type: "whatsapp" });
}

// Alias-Namen (Aufgaben-Definition) + Kompatibilität zu bestehenden Imports
export const trackCallClick = trackPhoneClick;
export const trackWhatsAppClick = trackWhatsappClick;
// Die Formular-Conversion feuert zentral über den Axios-Interceptor unten –
// dieser Alt-Export bleibt als bewusst leere Kompatibilitäts-Funktion bestehen.
export function trackLeadConversion() { /* zentral über Interceptor abgedeckt */ }

// ---- Globaler Formular-Interceptor -------------------------------------
// ALLE Lead-Formulare der Website senden an /api/leads*. Erst wenn das
// Backend mit 200/201 antwortet, feuert die Formular-Conversion – mit der
// Lead-ID als transaction_id und (falls im Formular vorhanden) E-Mail und
// Telefon als Erweiterte Conversion. Kein Feuern bei Klick/Validierungsfehler.
const extractContact = (data) => {
  const out = { email: "", phone: "" };
  const scan = (k, v) => {
    if (typeof v !== "string") return;
    if (!out.email && /e-?mail/i.test(k) && /\S+@\S+\.\S+/.test(v)) out.email = v.trim();
    if (!out.phone && /telefon|phone|mobil/i.test(k) && v.trim().length >= 5) out.phone = v.trim();
  };
  try {
    if (data instanceof FormData) {
      for (const [k, v] of data.entries()) scan(k, v);
    } else if (typeof data === "string") {
      const walk = (o, depth) => {
        if (!o || typeof o !== "object" || depth > 1) return;
        Object.entries(o).forEach(([k, v]) => (typeof v === "object" ? walk(v, depth + 1) : scan(k, v)));
      };
      walk(JSON.parse(data), 0);
    }
  } catch { /* leere Kontaktdaten sind ok */ }
  return out;
};

axios.interceptors.response.use(
  (res) => {
    try {
      const cfg = res.config || {};
      const url = cfg.url || "";
      if (
        (cfg.method || "").toLowerCase() === "post"
        && /\/leads(\/|$)/.test(url)
        && !url.includes("/admin")
        && (res.status === 200 || res.status === 201)
        && res.data && !res.data.error
      ) {
        const { email, phone } = extractContact(cfg.data);
        trackFormLead({ email, phone, leadId: res.data.id || "" });
      }
    } catch { /* Messung darf nie die Nutzung stören */ }
    return res;
  },
  (err) => Promise.reject(err),
);
